"use client";

import { shades } from "@/styles/theme";
import {
  Speed,
  Security,
  Public,
  Code,
  SmartToy,
  Support,
} from "@mui/icons-material";
import {
  Box,
  Grid,
  Typography,
  Container,
  Card,
  CardContent,
  useTheme,
  alpha,
} from "@mui/material";
import Image from "next/image";
import React from "react";

const ValueProposition = () => {
  const theme = useTheme();

  const valueProps = [
    {
      icon: <Speed sx={{ fontSize: { xs: 30, md: 35 } }} />,
      title: "Lightning-Fast Delivery",
      description:
        "Agile development process that delivers MVPs in weeks, not months. We understand time-to-market is critical for your business success.",
    },
    {
      icon: <Code sx={{ fontSize: { xs: 30, md: 35 } }} />,
      title: "Modern Tech Stack",
      description:
        "Built with cutting-edge technologies like Next.js, React, and cloud-native architectures ensuring scalability, performance, and security.",
    },
    {
      icon: <SmartToy sx={{ fontSize: { xs: 30, md: 35 } }} />,
      title: "AI & Automation Focus",
      description:
        "Intelligent solutions that automate repetitive tasks, reduce costs, and provide data-driven insights for smarter business decisions.",
    },
    {
      icon: <Public sx={{ fontSize: { xs: 30, md: 35 } }} />,
      title: "Global Expertise",
      description:
        "Serving clients across Netherlands, UK, and Europe with deep understanding of local markets and international business standards.",
    },
    {
      icon: <Security sx={{ fontSize: { xs: 30, md: 35 } }} />,
      title: "Enterprise Security",
      description:
        "GDPR-compliant solutions with enterprise-grade security protocols, ensuring your data and applications are always protected.",
    },
    {
      icon: <Support sx={{ fontSize: { xs: 30, md: 35 } }} />,
      title: "24/7 Dedicated Support",
      description:
        "Round-the-clock support across time zones. Your success never sleeps, and neither does our commitment to you.",
    },
  ];

  return (
    <Box
      sx={{
        py: { xs: 6, md: 10 },
        background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.02)} 0%, ${alpha(theme.palette.secondary.main, 0.02)} 100%)`,
        position: "relative",
      }}
    >
      {/* Background Pattern */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `radial-gradient(circle at 20% 80%, ${alpha(theme.palette.primary.main, 0.05)} 0%, transparent 50%),
                      radial-gradient(circle at 80% 20%, ${alpha(theme.palette.secondary.main, 0.05)} 0%, transparent 50%)`,
          zIndex: 0,
        }}
      />

      <Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
        {/* Header Section */}
        <Box textAlign="center" mb={{ xs: 6, md: 8 }}>
          <Typography
            variant="h2"
            component="h3"
            gutterBottom
            sx={{
              fontWeight: 700,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              backgroundClip: "text",
              WebkitBackgroundClip: "text",
              color: "transparent",
              fontSize: { xs: "2.5rem", md: "3rem" },
            }}
          >
            Why Choose LinXar Labs?
          </Typography>
          <Typography
            variant="h6"
            component={"h3"}
            // FIX: Improved contrast for subtitle
            sx={{
              maxWidth: 600,
              mx: "auto",
              fontSize: { xs: "1.1rem", md: "1.25rem" },
              lineHeight: 1.6,
              color: theme.palette.text.primary, // Changed from text.secondary for better contrast
              opacity: 0.8, // Use opacity instead of secondary color
            }}
          >
            We combine technical excellence with business understanding to
            deliver exceptional web development and automation solutions for
            global clients.
          </Typography>
        </Box>

        <Grid container spacing={4} alignItems="center">
          {/* Value Propositions Grid */}
          <Grid size={{ xs: 12, md: 7 }}>
            <Grid container spacing={3}>
              {valueProps.map((item, index) => (
                <Grid size={{ xs: 12, sm: 6 }} key={index}>
                  <Card
                    sx={{
                      height: "100%",
                      border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                      background: "white",
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.15)}`,
                        borderColor: alpha(theme.palette.primary.main, 0.3),
                      },
                    }}
                  >
                    <CardContent sx={{ p: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 3,
                          height: "100%",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            backgroundColor: alpha(
                              theme.palette.primary.main,
                              0.1
                            ),
                            borderRadius: "50%",
                            padding: 1,
                            flexShrink: 0,
                            color: theme.palette.primary.main,
                          }}
                        >
                          {item.icon}
                        </Box>
                        <Box>
                          <Typography
                            variant="h6"
                            component="h3"
                            gutterBottom
                            sx={{
                              fontWeight: 600,
                              fontSize: { xs: "1.1rem", md: "1.25rem" },
                              color: theme.palette.text.primary, // Ensure good contrast
                            }}
                          >
                            {item.title}
                          </Typography>
                          <Typography
                            variant="body1"
                            fontSize={{ xs: "0.9rem", md: "1rem" }}
                            // FIX: Improved contrast for body text
                            sx={{
                              lineHeight: 1.6,
                              color: theme.palette.text.primary,
                              opacity: 0.8, // Better than text.secondary for contrast
                            }}
                          >
                            {item.description}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Grid>

          {/* Image Section */}
          <Grid size={{ xs: 12, md: 5 }} mx={{ xs: 4, md: 0 }}>
            <Box
              sx={{
                position: "relative",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <Box
                sx={{
                  position: "relative",
                  width: { xs: 300, md: 400 },
                  height: { xs: 300, md: 400 },
                  borderRadius: 4,
                  overflow: "hidden",
                  boxShadow: `0 20px 40px ${alpha(theme.palette.common.black, 0.1)}`,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              >
                <Image
                  src="/infogra.png"
                  alt="LinXar Labs - Web Development and Automation Team"
                  fill
                  sizes="(max-width: 768px) 300px, 400px"
                  style={{
                    objectFit: "cover",
                  }}
                  priority
                />

                {/* Overlay Gradient */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.1)} 0%, ${alpha(theme.palette.secondary.main, 0.1)} 100%)`,
                  }}
                />
              </Box>

              {/* Floating Stats */}
              <Box
                sx={{
                  position: "absolute",
                  top: "-5%",
                  right: "-3%",
                  backgroundColor: "white",
                  borderRadius: 2,
                  padding: 2,
                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                  animation: "float 3s ease-in-out infinite",
                  "@keyframes float": {
                    "0%, 100%": { transform: "translateY(0px)" },
                    "50%": { transform: "translateY(-10px)" },
                  },
                }}
              >
                <Typography
                  variant="h4"
                  component="div"
                  color="primary"
                  fontWeight={700}
                >
                  Timely
                </Typography>
                <Typography
                  variant="body2"
                  // FIX: Improved contrast for stat labels
                  sx={{
                    color: theme.palette.text.primary,
                    opacity: 0.8,
                    fontWeight: 500,
                  }}
                >
                  Projects Delivery
                </Typography>
              </Box>

              <Box
                sx={{
                  position: "absolute",
                  bottom: "-3%",
                  left: "-5%",
                  backgroundColor: "white",
                  borderRadius: 2,
                  padding: 2,
                  boxShadow: "0 8px 24px rgba(0, 0, 0, 0.12)",
                  border: `1px solid ${alpha(theme.palette.secondary.main, 0.1)}`,
                  animation: "float 3s ease-in-out infinite 1.5s",
                  "@keyframes float": {
                    "0%, 100%": { transform: "translateY(0px)" },
                    "50%": { transform: "translateY(-10px)" },
                  },
                }}
              >
                <Typography
                  variant="h4"
                  component="div"
                  color={`${shades.secondary[600]}`}
                  fontWeight={700}
                >
                  98%
                </Typography>
                <Typography
                  variant="body2"
                  // FIX: Improved contrast for stat labels
                  sx={{
                    color: theme.palette.text.primary,
                    opacity: 0.8,
                    fontWeight: 500,
                  }}
                >
                  Client Satisfaction
                </Typography>
              </Box>
            </Box>
          </Grid>
        </Grid>

        {/* Trust Badges Section */}
        <Box
          sx={{
            mt: 8,
            textAlign: "center",
            p: 4,
            borderRadius: 4,
            backgroundColor: alpha(theme.palette.primary.main, 0.05), // Slightly darker for better contrast
            border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
          }}
        >
          <Typography
            variant="h6"
            component="h3"
            gutterBottom
            // FIX: Improved contrast for section title
            sx={{
              color: theme.palette.text.primary,
              opacity: 0.9,
              fontWeight: 600,
            }}
          >
            Trusted by Businesses Across Europe
          </Typography>
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 4,
              flexWrap: "wrap",
              mt: 2,
            }}
          >
            {[
              "GDPR Compliant",
              "SLA Guarantee",
              "24/7 Support",
              "ISO Certified",
            ].map((badge, index) => (
              <Box
                key={index}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  padding: "8px 16px",
                  backgroundColor: "white",
                  borderRadius: 2,
                  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: "50%",
                    backgroundColor: theme.palette.success.dark,
                  }}
                />
                <Typography
                  variant="body2"
                  fontWeight={500}
                  // FIX: Improved contrast for badge text
                  sx={{ color: theme.palette.text.primary }}
                >
                  {badge}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default ValueProposition;
